import React from 'react'
import { cn } from '@/lib/utils'

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'filled' | 'text' | 'ghost'
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'icon-sm' | 'icon-md' | 'icon-lg'
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      className,
      children,
      icon,
      iconPosition = 'left',
      ...props
    },
    ref,
  ) => {
    const baseStyles =
      'inline-flex items-center justify-center transition-colors focus:outline-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'

    const variantStyles = {
      primary:
        'bg-(--primary-teal) text-white hover:bg-(--primary-teal-hover) focus:ring-2 focus:ring-(--primary-teal) focus:ring-offset-1 font-medium',
      secondary:
        'border border-(--color-border-input) text-(--color-text-secondary) hover:bg-(--color-hover-subtle) focus:ring-2 focus:ring-(--primary-teal) focus:ring-offset-1 font-medium',
      filled:
        'bg-(--primary-teal) text-white hover:opacity-90 focus:ring-2 focus:ring-(--primary-teal) focus:ring-offset-1 font-medium',
      text: 'text-(--primary-teal) hover:text-(--primary-teal-hover) focus:outline-none',
      ghost:
        'text-(--color-text-secondary) hover:bg-(--color-hover-subtle) hover:text-(--color-text-primary) focus:ring-2 focus:ring-(--primary-teal) focus:ring-offset-1',
    }

    const sizeStyles = {
      sm: 'px-3.5 py-1.5 text-body-sm rounded-lg',
      md: 'px-4 py-2 text-body-sm rounded-lg',
      lg: 'px-5 py-2.5 text-body rounded-lg',
      xl: 'px-6 py-3 text-body-lg rounded-xl',
      'icon-sm': 'h-6 w-6 p-0 rounded-lg',
      'icon-md': 'h-7 w-7 p-0 rounded-lg',
      'icon-lg': 'h-8 w-8 p-0 rounded-md',
    }

    return (
      <button
        ref={ref}
        className={cn(
          baseStyles,
          variantStyles[variant],
          sizeStyles[size as keyof typeof sizeStyles],
          className,
        )}
        style={{ outline: 'none', ...props.style }}
        {...props}
      >
        {icon || children ? (
          <span className="flex items-center gap-1.5">
            {icon && iconPosition === 'left' && icon}
            {children}
            {icon && iconPosition === 'right' && icon}
          </span>
        ) : null}
      </button>
    )
  },
)

Button.displayName = 'Button'

export default Button
